import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/common";
import { IosSteps } from "@/components/notify/IosInstallHint";
import { disablePush, enablePush, isIOS, isStandalone, pushIsOn, pushSupported } from "@/lib/push";

const AMBER = "var(--st-amber)";

import AlertPrefs from "@/components/notify/AlertPrefs";

export default function NotificationSettings() {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { pushIsOn().then(setOn); }, []);

  const flip = async (v) => {
    setError("");
    setBusy(true);
    try {
      if (v) { await enablePush(); toast.success("Browser notifications are on"); } else { await disablePush(); toast.success("Browser notifications are off"); }
      setOn(v);
    } catch (e) {
      setOn(false);
      setError(e.message === "denied" ? "blocked" : e.message === "unsupported" ? "unsupported" : "failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="notification-settings-page">
      <PageHeader eyebrow="My account" title="Notifications" rgb={AMBER} testid="notification-settings-title" />
      <section className="q-card max-w-2xl space-y-5 q-rise">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full" style={{ background: `rgba(${AMBER},0.14)` }}>
            {on ? <Bell className="h-5 w-5" style={{ color: `rgb(${AMBER})` }} /> : <BellOff className="h-5 w-5 text-q-faint" />}
          </span>
          <div className="min-w-0 flex-1">
            <label htmlFor="push-toggle" className="font-display text-lg font-extrabold text-q-text">Browser notifications</label>
            <p className="mt-1 text-sm text-q-muted">Get a ping on this browser when someone adds to a list you follow or are shopping, even when The Queue isn't open.</p>
          </div>
          {busy ? <Loader2 className="mt-2 h-5 w-5 animate-spin text-q-muted" /> : (
            <Switch id="push-toggle" checked={!!on} disabled={on === null} onCheckedChange={flip} data-testid="push-toggle" data-state-on={on ? "1" : "0"} />
          )}
        </div>
        {error === "blocked" && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm" data-testid="push-blocked-message">
            <p className="font-bold text-red-200">Notifications are blocked in your browser settings</p>
            <p className="mt-1 text-q-muted" data-testid="push-blocked-help">To fix it, click the lock or settings icon next to the web address, set Notifications to "Allow", then reload this page and turn the switch on again.</p>
          </div>
        )}
        {error === "unsupported" && <p className="q-error" data-testid="push-unsupported-message">This browser doesn't support notifications.{isIOS() && !isStandalone() ? " Add The Queue to your Home Screen first." : ""}</p>}
        {error === "failed" && <p className="q-error" data-testid="push-failed-message">We couldn't turn on notifications for this browser. Please try again.</p>}
        {isIOS() && !isStandalone() && <IosSteps />}
        <p className="text-xs text-q-faint">In-app alerts (the bell and pop-up toasts) are always on while The Queue is open.{!pushSupported() && !isIOS() ? " This browser can't receive background notifications." : ""}</p>
      </section>
      <AlertPrefs />
    </div>
  );
}
